import { openDB, type IDBPDatabase } from 'idb';
import { submit, type SubmitArgs } from '../api/rpc';
import type { SubmitResult } from '../api/types';
import { RpcError, toRpcError } from '../api/errors';
import { photoPath, uploadPhoto } from '../api/storage';
import { session } from './session';
import { Emitter } from './emitter';

/**
 * 写真の提出。圏外なら写真と撮った時刻を端末（IndexedDB）に残し、電波が戻ると自動で送る（図鑑㉛d）。
 * - パスは最初に1回だけ決めて保存する。再送しても同じパスなので写真は二重にならない
 * - サーバーで成立したのに返事だけ失われた場合、再送は already_submitted になる＝送れたとみなす
 * - client_action_at は撮った時刻（圏外で遅れて届いても、受信時刻と分けて記録される）
 * - 圏外以外のエラー（襲来中・提出済みなど）は残さない。襲来中に撮った写真が後で通らないように
 */
export interface PendingSubmit {
  id: string;
  team_id: string;
  kind: 'spot' | 'prompt';
  target_id: number;
  path: string;
  blob: Blob;
  uploaded: boolean;
  client_action_at: string;
  lat: number | null;
  lon: number | null;
  acc: number | null;
  created_at: number;
  attempts: number;
}

export type SendOutcome =
  | { status: 'sent'; result: SubmitResult; item: PendingSubmit }
  | { status: 'queued'; item: PendingSubmit }
  | { status: 'rejected'; error: RpcError; item: PendingSubmit };

const DB = 'waggle-outbox';
const STORE = 'pending';
let dbp: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  dbp ??= openDB(DB, 1, {
    upgrade(d) {
      d.createObjectStore(STORE, { keyPath: 'id' });
    },
  });
  return dbp;
}

class Outbox {
  /** 後から送れた・断られた提出（圏外から戻ったとき）。画面はこれでダイスや知らせを出す */
  readonly settled = new Emitter<SendOutcome>();
  readonly countChanged = new Emitter<number>();
  private flushing = false;

  newItem(a: Omit<PendingSubmit, 'id' | 'path' | 'uploaded' | 'created_at' | 'attempts' | 'team_id'> & { event_id: string; team_id: string }): PendingSubmit {
    return {
      id: crypto.randomUUID(),
      team_id: a.team_id,
      kind: a.kind,
      target_id: a.target_id,
      path: photoPath(a.event_id, a.team_id),
      blob: a.blob,
      uploaded: false,
      client_action_at: a.client_action_at,
      lat: a.lat,
      lon: a.lon,
      acc: a.acc,
      created_at: Date.now(),
      attempts: 0,
    };
  }

  /** 1件送る。onProgress は写真の送信の割合（0〜1） */
  async send(item: PendingSubmit, onProgress?: (r: number) => void): Promise<SendOutcome> {
    item.attempts++;
    try {
      if (!item.uploaded) {
        await uploadPhoto(item.path, item.blob, onProgress);
        item.uploaded = true;
      }
      onProgress?.(1);
      const args: SubmitArgs = {
        kind: item.kind,
        targetId: item.target_id,
        imagePath: item.path,
        clientActionAt: item.client_action_at,
        lat: item.lat,
        lon: item.lon,
        acc: item.acc,
      };
      const result = await submit(args);
      await this.remove(item.id);
      return { status: 'sent', result, item };
    } catch (e) {
      const err = toRpcError(e);
      if (err.isNetwork) {
        await this.put(item);
        return { status: 'queued', item };
      }
      await this.remove(item.id);
      return { status: 'rejected', error: err, item };
    }
  }

  async pending(): Promise<PendingSubmit[]> {
    const team = session.get()?.team_id;
    const all = (await (await db()).getAll(STORE)) as PendingSubmit[];
    return all.filter((p) => p.team_id === team).sort((a, b) => a.created_at - b.created_at);
  }

  /** 残っている提出を古い順に送る。電波が戻ったとき・起動時・「今すぐ送り直す」で呼ぶ */
  async flush(): Promise<void> {
    if (this.flushing || !navigator.onLine || !session.token()) return;
    this.flushing = true;
    try {
      for (const item of await this.pending()) {
        const out = await this.send(item);
        if (out.status === 'queued') break;
        this.settled.emit(out);
      }
    } finally {
      this.flushing = false;
      this.countChanged.emit((await this.pending()).length);
    }
  }

  start(): void {
    window.addEventListener('online', () => void this.flush());
    setInterval(() => void this.flush(), 30_000);
    void this.flush();
  }

  private async put(item: PendingSubmit): Promise<void> {
    await (await db()).put(STORE, item);
    this.countChanged.emit((await this.pending()).length);
  }
  private async remove(id: string): Promise<void> {
    await (await db()).delete(STORE, id);
  }
}

export const outbox = new Outbox();
